import type { FlightProcessingStatus } from "@ca/types/flights";
import type { MaybeRefOrGetter } from "vue";

import { toValue } from "vue";

import {
	useMutation, useQuery, useQueryClient, 
} from "@tanstack/vue-query";

import { client } from "@/api";

import { PROCESSING_RETRY, processingRefetchInterval } from "./processing";

export const useFlightApproach = (flightId: string) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.flights({ flightId }).approach.get();
		return {
			groundReference: resp.data?.groundReference ?? null,
			samples: resp.data?.approach ?? [],
		};
	},
	queryKey: ["flight-approach", flightId],
	refetchOnMount: false,
});

export const useFlightProcessing = (flightId: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.flights({
			flightId: toValue(flightId),
		}).processing.get();
		if (resp.error) {
			throw resp.error;
		}
		return (resp.data?.status ?? null) as null | FlightProcessingStatus;
	},
	enabled: () => Boolean(toValue(flightId)),
	queryKey: ["flight-processing", flightId],
	refetchInterval: processingRefetchInterval,
	retry: PROCESSING_RETRY,
});

export const useFlightById = (flightId: string) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.flights({ flightId }).get();
		return resp.data?.flight ?? null;
	},
	queryKey: ["flight-id", flightId],
	refetchOnMount: false,
});

export const useFlightRunways = (flightId: string) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.flights({ flightId }).runways.get();
		const fallback = {
			destination: null,
			origin: null,
		};
		if (resp.status !== 200) {
			return fallback;
		}
		return resp.data?.runways ?? fallback;
	},
	queryKey: ["flight-runways", flightId],
	refetchOnMount: false,
});

export const useFlightTrack = (flightId: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resolvedFlightId = toValue(flightId);
		const resp = await client.api.v2.admin.flights({ flightId: resolvedFlightId }).track.get();
		return resp.data;
	},
	queryKey: ["flight-track", flightId],
});

export const useFlightMetars = (flightId: string) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.flights({ flightId }).metar.get();
		return resp.data?.metars ?? {
			origin: null,
			destination: null,
		};
	},
	queryKey: ["flight-metars", flightId],
});

export const useDeleteFlightById = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({
			flightId,
			aircraftId,
		}: {
			flightId: string;
			aircraftId?: MaybeRefOrGetter<string | undefined>;
		}) => {
			const resolvedAircraftId = toValue(aircraftId);
			const response = await client.api.v1.admin.flights({ flightId }).delete();
			if (response.status !== 200) {
				throw new Error((response.error as { value?: { message?: string } })?.value?.message ?? "Failed to delete flight");
			}
			return {
				aircraftId: resolvedAircraftId,
				deletedCount: response.data?.deletedCount ?? 0,
			};
		},
		onSuccess: ({ aircraftId }, { flightId }) => {
			queryClient.invalidateQueries({ queryKey: ["flight-id", flightId] });
			queryClient.invalidateQueries({ queryKey: ["aircraft"] });
			queryClient.invalidateQueries({ queryKey: ["flight-id"] });
			if (aircraftId) {
				queryClient.invalidateQueries({ queryKey: ["flights", aircraftId] });
				queryClient.invalidateQueries({ queryKey: ["flight-summaries", aircraftId] });
				queryClient.invalidateQueries({ queryKey: ["stats", aircraftId] });
				queryClient.invalidateQueries({ queryKey: ["missions", aircraftId] });
				return;
			}
			queryClient.invalidateQueries({ queryKey: ["flights"] });
			queryClient.invalidateQueries({ queryKey: ["flight-summaries"] });
			queryClient.invalidateQueries({ queryKey: ["stats"] });
			queryClient.invalidateQueries({ queryKey: ["missions"] });
		},
	});
};


export const recalcFlightRisk = (flightId: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async () => {
			await client.api.v1.flights({ flightId }).risk._recalc.post();
			for (const queryKey of [
				["flight-id", flightId],
				["flight-processing", flightId],
			]) {
				queryClient.invalidateQueries({ queryKey });
			}
		},
		mutationKey: ["recalc-flight-metars", flightId],
	});
};

export const useReassessFlight = (flightId: string) => {
	const queryClient = useQueryClient();
	const toast = useToast();
	return useMutation({
		mutationFn: async () => {
			const resp = await client.api.v1.admin.flights({ flightId })._reassess.post();
			const data = resp.data;
			if (resp.status !== 200 || data === null || !("outcome" in data)) {
				throw new Error("Failed to reassess this flight");
			}
			return data;
		},
		mutationKey: ["reassess-flight", flightId],
		onError: () => {
			toast.add({
				color: "error",
				description: "The flight could not be reassessed. Please try again.",
				title: "Reassessment failed",
			});
		},
		onSuccess: (result) => {
			if (result.outcome === "running") {
				toast.add({
					color: "warning",
					description: "This flight is already being reassessed.",
					title: "Reassessment in progress",
				});
				return;
			}
			if (result.outcome === "no_track") {
				toast.add({
					color: "warning",
					description: "There is no stored track for this flight to score.",
					title: "Nothing to reassess",
				});
				return;
			}
			const { eventCount } = result;
			toast.add(eventCount === null
				? {
					color: "warning",
					description: "Risk was recalculated, but the approach could not be scored. Any stored sink rate events were kept.",
					title: "Sink rate scoring skipped",
				}
				: {
					color: "success",
					description: `Scored ${eventCount} sink rate ${eventCount === 1 ? "event" : "events"} and recalculated risk.`,
					title: "Flight reassessed",
				});
			for (const queryKey of [
				["flight-approach", flightId],
				["flight-id", flightId],
				["flight-processing", flightId],
				["flight-track", flightId],
			]) {
				queryClient.invalidateQueries({ queryKey });
			}
		},
	});
};

export const useUpdateFlightRunway = (flightId: string) => {
	const queryClient = useQueryClient();
	const toast = useToast();
	return useMutation({
		mutationFn: async (payload: {
			runwayId: string;
			runwayIdentifier: string;
			side: "destination" | "origin";
		}) => {
			const resp = await client.api.v1.admin.flights({ flightId }).runways.put(payload);
			if (resp.status !== 200) {
				toast.add({
					color: "error",
					description: "Failed to update runway",
					title: "Error",
				});
				return null;
			} else { 
				toast.add({
					color: "success",
					description: `${payload.side === "origin" ? "Origin" : "Destination"} runway updated. Risk recalculation queued.`,
					title: "Runway Updated",
				});
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["flight-id", flightId],
			});
			queryClient.invalidateQueries({
				queryKey: ["flight-runways", flightId],
			});
		},
		mutationKey: ["flight-runway-update", flightId],
	});
};


export const useAirsyncFlightApproach = (flightId: string) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.flights({ flightId }).airsync.get();
		return resp.data ?? null;
	},
	queryKey: ["airsync-flight-approach", flightId],
});

export const useFlightPireps = (flightId: string) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.flights({ flightId }).pireps.get();
		return resp.data?.pireps ?? [];
	},
	queryKey: ["flight-pireps", flightId],
	refetchOnMount: false,
});

export const useFlightComments = (flightId: string) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.flights({ flightId }).comments.get();
		return resp.data?.comments ?? [];
	},
	queryKey: ["flight-comments", flightId],
});
